"use client";

import { useInfiniteQuery } from "@tanstack/react-query";
import type { Address } from "viem";

import { TTL } from "@/config/ttl";
import type { SwapTransactionsPage } from "@/domain/types";
import { apiFetch } from "@/lib/apiFetch";

type Cursor = Record<string, string | number> | null;

async function fetchPage(cursor: Cursor, wallet: Address | undefined): Promise<SwapTransactionsPage> {
  const params = new URLSearchParams(cursor ? Object.entries(cursor).map(([k, v]) => [k, String(v)]) : []);
  if (wallet) params.set("wallet", wallet);
  const query = params.size ? `?${params.toString()}` : "";
  return apiFetch<SwapTransactionsPage>(`/api/transactions${query}`);
}

/** Global swap/add/remove feed when `wallet` is omitted, or that wallet's
 * own activity when given (Portfolio → Activity). */
export function useSwapTransactions(wallet?: Address) {
  return useInfiniteQuery({
    queryKey: ["swap-transactions", wallet ?? "all"],
    queryFn: ({ pageParam }) => fetchPage(pageParam, wallet),
    initialPageParam: null as Cursor,
    getNextPageParam: (lastPage) => lastPage.nextPageParams,
    staleTime: TTL.transactions,
    // Keep the first page live like a real activity feed; loaded extra
    // pages beyond that just sit until the user asks for more.
    refetchInterval: TTL.transactions,
  });
}
